import React, { useEffect } from "react";
import styled from "styled-components";
import { Alert, Button } from "@mui/material";
import {
  ProductVariant,
  ResultsRow,
  ResultsRowStatus,
  PermissionKeys,
  PermissionRequired,
  formatToDateOnly,
} from "lib-core";
import LibraryTable, {
  LibraryTableHeaders,
} from "../components/LibraryTable/LibraryTable";
import ProductVariantSnapshotBox from "../components/snaphostBoxes/ProductVariantSnapshot";
import RecordingProgress from "../components/RecordingProgress";
import useProductVariantResults from "./productVariantResults-hook";
import { testResultHeaders, TestResultWithButton } from "./helpers";
import { TableRowSet } from "../components/LibraryTable/LibraryTableBody";
import LibrarySubheader from "./LibrarySubheader";
import LoadingIndicator from "../components/LoadingIndicator/LoadingIndicator";
import BlendedSnapshotBox from "../components/snaphostBoxes/BlendedSnapshotBox";

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 2rem;
`;

const HeaderColspan = 11;

interface ResultsTabProps {
  productVariant: ProductVariant;
}

const { readProductLibrary, readAuthentifieldDummyProduct } = PermissionKeys;

const ResultsTab: (props: ResultsTabProps) => JSX.Element = ({
  productVariant,
}) => {
  const {
    fetchResults,
    downloadManufacturingCsv,
    loadingResults,
    results,
    csvIndex,
    csvRequestBusy,
  } = useProductVariantResults();

  useEffect(() => {
    fetchResults(productVariant.id);
  }, [productVariant]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadButton = (
    metadataUrl: string,
    manufacturingSite: string,
    rowIndex: number
  ) => {
    const productIsDummy = productVariant?.metadata.dummyVariant;

    const key = productIsDummy
      ? readAuthentifieldDummyProduct
      : readProductLibrary;

    return (
      <PermissionRequired permissionKeys={[key]}>
        {csvIndex === rowIndex ? (
          <LoadingIndicator />
        ) : (
          <Button
            disabled={csvRequestBusy || !metadataUrl}
            variant="outlined"
            size="small"
            onClick={() =>
              downloadManufacturingCsv(metadataUrl, manufacturingSite, rowIndex)
            }
          >
            Download
          </Button>
        )}
      </PermissionRequired>
    );
  };

  const getRows = (rows: ResultsRow[]) => {
    // Flatten rows to create one row per material number
    // Use flatMap to handle cases where a manufacturing site may correspond to multiple material numbers.
    // This ensures that each material number is represented as a separate row in the output.
    return rows.flatMap((resultsRow, rowIndex) => {
      const {
        manufacturingSite,
        metadataUrl,
        coverageRate: coverageRateMapping,
        firstMeasurementAt,
      } = resultsRow;

      const siteCoverageRates =
        // Manufacturing site is guaranteed to be in the coverageRateMapping
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        coverageRateMapping[manufacturingSite]!;

      const materialNumbers = Object.keys(siteCoverageRates);

      return materialNumbers.map((materialNumber) => {
        const coverageRate =
          // Coverage rate is guaranteed to be in the coverageRateMappingForThisManufacturingSite
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          siteCoverageRates[materialNumber]!;
        return {
          ...resultsRow,
          materialNumber,
          coverageRate: Number.isFinite(coverageRate)
            ? `${parseFloat((coverageRate * 100).toFixed(2))}%`
            : "0%",
          date: firstMeasurementAt ? formatToDateOnly(firstMeasurementAt) : "",
          metadata: downloadButton(metadataUrl, manufacturingSite, rowIndex),
          originalRowIndex: rowIndex,
        };
      });
    });
  };

  const resultsRowStatuses = [
    [ResultsRowStatus.NoMeasurements, ""],
    [ResultsRowStatus.Active, "Active"],
    [ResultsRowStatus.Inactive, "Inactive"],
  ];

  const mappedResults = (results.rows ?? []).map((row) => ({
    ...row,
  }));

  const manufacturingSites = Object.keys(results.testCoverageRate ?? {});

  /**
   * If a manufacturing site does not have any measurements,
   * The backend api will not be able to generate a summary for such site,
   * hence there is no "row" for it in the results.
   * We need to create a row for it with the status "NoMeasurements"
   * and the coverage rate of the test results.
   * This is done to ensure that the table displays all manufacturing sites
   * even if they do not have any measurements.
   */
  const manufacturingSitesThatHaveNoSummary = manufacturingSites.filter(
    (site) => results.rows.every((row) => row.manufacturingSite !== site)
  );

  const rowSets: TableRowSet<TestResultWithButton>[] = resultsRowStatuses.map(
    ([status, title]) => {
      if (status === ResultsRowStatus.NoMeasurements) {
        return {
          label: [],
          rows: getRows(
            manufacturingSitesThatHaveNoSummary.map((site) => {
              return {
                manufacturingSite: site,
                coverageRate: results.testCoverageRate,
                firstMeasurementAt: "",
                metadataUrl: "",
                measurementCount: 0,
                status,
              } as ResultsRow;
            })
          ),
        };
      }
      const rows = mappedResults.filter(
        (row: ResultsRow) => row.status === status
      );
      return {
        label: [
          {
            label: `${title} (${rows.length})`,
            colspan: HeaderColspan,
          },
        ] as LibraryTableHeaders<Record<string, number>>,
        rows: getRows(rows),
      };
    }
  );

  const createSubheaderTitle = () => {
    if (Number.isFinite(results.rows?.length)) {
      if (results.rows.length === 0) {
        return "No test results available";
      }
      if (results.rows.length === 1) {
        return `Test results from 1 manufacturing site`;
      }
      return `Test results from ${results.rows.length} manufacturing sites`;
    }
    return "No test results available";
  };

  return (
    <>
      <LibrarySubheader
        loading={loadingResults}
        loadingTitle="Loading test results..."
        title={createSubheaderTitle()}
      />

      <LibraryTable
        loading={loadingResults}
        headers={testResultHeaders}
        rowSets={rowSets}
      />

      <FooterContainer>
        {!!results.rows && <RecordingProgress results={results} />}
        <BlendedSnapshotBox />
        <ProductVariantSnapshotBox
          productVariantId={productVariant.id}
          results={results}
        />
      </FooterContainer>
      {manufacturingSitesThatHaveNoSummary.length > 0 && (
        <Alert severity="info" sx={{ marginTop: "1rem" }}>
          Some materials were not measured, so the system could not classify
          them as Active or Inactive.
        </Alert>
      )}
    </>
  );
};

export default ResultsTab;
